/* 11gradi — Theme JS */
'use strict';

// ── Cart Drawer ────────────────────────────────────────────────
class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('cart-drawer');
    this.overlay = document.getElementById('overlay');
    if (!this.drawer) return;

    // Delegazione sul document: i controlli open/close devono continuare a
    // funzionare anche dopo che refresh() rigenera il contenuto del drawer.
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-open]')) this.open();
      else if (e.target.closest('[data-cart-close]')) this.close();
    });
    this.overlay?.addEventListener('click', () => this.close());

    document.addEventListener('cart:updated', () => this.refresh());
  }

  open() {
    this.drawer.classList.add('open');
    this.overlay?.classList.add('active');
    document.body.classList.add('no-scroll');
  }

  close() {
    this.drawer.classList.remove('open');
    this.overlay?.classList.remove('active');
    document.body.classList.remove('no-scroll');
  }

  async refresh() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this.updateCount(cart.item_count);
      this.updateSubtotal(cart.total_price);

      const sectionRes = await fetch(`${window.Shopify?.routes?.root || '/'}?section_id=cart-drawer`);
      const html = await sectionRes.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newDrawer = doc.getElementById('cart-drawer');
      // Sostituisce l'intero contenuto (lista articoli + footer con totale e
      // checkout), così il drawer si aggiorna anche nel passaggio da carrello
      // vuoto a pieno. Contenuto generato da Shopify (origine fidata), non input utente.
      if (newDrawer) this.drawer.innerHTML = newDrawer.innerHTML;
    } catch (e) {}
  }

  updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      const prev = parseInt(el.dataset.count || '0', 10);
      el.textContent = count;
      el.dataset.count = count;
      el.style.display = count > 0 ? 'flex' : 'none';
      if (count > prev) {
        el.classList.remove('bump');
        // force reflow to restart animation
        void el.offsetWidth;
        el.classList.add('bump');
      }
    });
  }

  updateSubtotal(totalCents) {
    const formatted = (totalCents / 100).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
    document.querySelectorAll('[data-cart-subtotal]').forEach(el => el.textContent = formatted);
  }
}

// ── Add to Cart ────────────────────────────────────────────────
class AddToCart {
  constructor() {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('[data-atc-form]');
      if (!form) return;
      e.preventDefault();

      const btn = form.querySelector('[data-atc-btn]');
      if (!btn) return;

      btn.disabled = true;
      const originalText = btn.textContent;
      btn.textContent = 'Aggiunta...';

      try {
        const formData = new FormData(form);
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: formData.get('id'),
            quantity: parseInt(formData.get('quantity') || 1)
          })
        });
        if (!res.ok) throw new Error('Add to cart failed');
        btn.textContent = 'Aggiunto ✓';
        setTimeout(() => { btn.textContent = originalText; btn.disabled = false; }, 1800);
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.querySelector('[data-cart-open]')?.click?.();
      } catch {
        btn.textContent = 'Errore — riprova';
        setTimeout(() => { btn.textContent = originalText; btn.disabled = false; }, 2000);
      }
    });
  }
}

// ── Mobile Nav ─────────────────────────────────────────────────
class MobileNav {
  constructor() {
    this.btn = document.getElementById('hamburger');
    this.nav = document.getElementById('mobile-nav');
    this.overlay = document.getElementById('overlay');
    if (!this.btn || !this.nav) return;

    this.btn.addEventListener('click', () => this.toggle());
    this.overlay?.addEventListener('click', () => this.close());
  }

  toggle() {
    const open = this.nav.classList.toggle('open');
    this.btn.classList.toggle('open', open);
    this.overlay?.classList.toggle('active', open);
    document.body.classList.toggle('no-scroll', open);
  }

  close() {
    this.nav.classList.remove('open');
    this.btn.classList.remove('open');
    this.overlay?.classList.remove('active');
    document.body.classList.remove('no-scroll');
  }
}

// ── Quantity Stepper ───────────────────────────────────────────
class QuantityStepper {
  constructor() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-qty-btn]');
      if (!btn) return;
      const wrapper = btn.closest('.quantity-stepper');
      const input = wrapper?.querySelector('[data-qty-input]');
      if (!input) return;
      const delta = btn.dataset.qtyBtn === '+' ? 1 : -1;
      const min = parseInt(input.min || 1);
      const max = parseInt(input.max || 999);
      input.value = Math.min(max, Math.max(min, parseInt(input.value) + delta));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
}

// ── Variant Selector (PDP) ─────────────────────────────────────
class VariantSelector {
  constructor() {
    const form = document.getElementById('product-form');
    if (!form) return;

    this.form = form;
    this.variantInput = form.querySelector('[name="id"]');
    this.priceEl = document.getElementById('product-price');
    this.comparePriceEl = document.getElementById('product-compare-price');
    this.stockEl = document.getElementById('product-stock');
    this.productData = this.getProductData();

    form.querySelectorAll('[data-variant-option]').forEach(btn => {
      btn.addEventListener('click', () => this.handleOptionChange(btn));
    });
  }

  getProductData() {
    const el = document.getElementById('product-json');
    try { return el ? JSON.parse(el.textContent) : null; } catch { return null; }
  }

  handleOptionChange(btn) {
    const group = btn.dataset.variantOption;
    const val = btn.dataset.value;

    btn.closest('[data-option-group]')?.querySelectorAll('[data-variant-option]').forEach(b => {
      b.classList.toggle('active', b.dataset.value === val);
    });

    this.updateVariant();
  }

  getSelectedOptions() {
    const opts = {};
    this.form.querySelectorAll('[data-option-group]').forEach((group, idx) => {
      const active = group.querySelector('[data-variant-option].active');
      if (active) opts[`option${idx + 1}`] = active.dataset.value;
    });
    return opts;
  }

  updateVariant() {
    if (!this.productData) return;
    const selected = this.getSelectedOptions();
    const variant = this.productData.variants.find(v =>
      Object.entries(selected).every(([k, val]) => v[k] === val)
    );
    if (!variant) return;

    this.variantInput.value = variant.id;
    if (this.priceEl) this.priceEl.textContent = this.formatMoney(variant.price);
    if (this.comparePriceEl && variant.compare_at_price > variant.price) {
      this.comparePriceEl.textContent = this.formatMoney(variant.compare_at_price);
      this.comparePriceEl.style.display = '';
    } else if (this.comparePriceEl) {
      this.comparePriceEl.style.display = 'none';
    }
    if (this.stockEl) {
      this.stockEl.textContent = variant.available ? 'Disponibile' : 'Esaurito';
    }

    const atcBtn = this.form.querySelector('[data-atc-btn]');
    if (atcBtn) atcBtn.disabled = !variant.available;
  }

  formatMoney(cents) {
    return '€ ' + (cents / 100).toLocaleString('it-IT', { minimumFractionDigits: 2 });
  }
}

// ── Gallery (PDP) ──────────────────────────────────────────────
class ProductGallery {
  constructor() {
    const main = document.getElementById('gallery-main');
    if (!main) return;
    this.activeIndex = 0;

    document.querySelectorAll('[data-gallery-thumb]').forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.select(thumb, i));
    });
  }

  select(thumb, idx) {
    const mainImg = document.getElementById('gallery-main-img');
    if (mainImg) mainImg.src = thumb.dataset.gallerySrc || thumb.src;

    document.querySelectorAll('[data-gallery-thumb]').forEach((t, i) => {
      t.parentElement.classList.toggle('active', i === idx);
    });
    this.activeIndex = idx;
    document.dispatchEvent(new CustomEvent('gallery:index', { detail: { index: idx } }));
  }
}

// ── Lightbox (PDP zoom) ────────────────────────────────────────
class Lightbox {
  constructor() {
    const box = document.querySelector('[data-lightbox]');
    if (!box) return;
    try {
      this.images = JSON.parse(box.dataset.images || '[]');
    } catch { this.images = []; }
    if (!this.images.length) return;

    this.box = box;
    this.img = box.querySelector('.lightbox__img');
    this.counter = box.querySelector('[data-lightbox-count]');
    this.idx = 0;

    document.getElementById('gallery-main')?.addEventListener('click', () => {
      const thumbs = [...document.querySelectorAll('[data-gallery-thumb]')];
      const active = document.querySelector('.product-gallery__thumb.active [data-gallery-thumb]');
      this.idx = active ? thumbs.indexOf(active) : 0;
      if (this.idx < 0) this.idx = 0;
      this.open();
    });

    box.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.close());
    box.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => this.go(-1));
    box.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.go(1));
    this.img?.addEventListener('click', (e) => { e.stopPropagation(); this.close(); });
    box.addEventListener('click', (e) => { if (e.target === box) this.close(); });

    document.addEventListener('keydown', (e) => {
      if (!box.classList.contains('open')) return;
      if (e.key === 'Escape') this.close();
      else if (e.key === 'ArrowLeft') this.go(-1);
      else if (e.key === 'ArrowRight') this.go(1);
    });

    document.addEventListener('gallery:index', (e) => {
      this.idx = e.detail.index;
      if (box.classList.contains('open')) this.render();
    });
  }

  open() {
    this.render();
    this.box.classList.add('open');
    this.box.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');
  }
  close() {
    this.box.classList.remove('open');
    this.box.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('no-scroll');
  }
  go(delta) {
    this.idx = (this.idx + delta + this.images.length) % this.images.length;
    this.render();
  }
  render() {
    this.img.src = this.images[this.idx];
    if (this.counter) this.counter.textContent = `${this.idx + 1} / ${this.images.length}`;
  }
}

// ── Accordion ──────────────────────────────────────────────────
class Accordion {
  constructor() {
    document.querySelectorAll('.accordion__header').forEach(header => {
      header.addEventListener('click', () => {
        const item = header.closest('.accordion__item');
        item.classList.toggle('open');
      });
    });
  }
}

// ── Filter sidebar (Collection) ────────────────────────────────
class CollectionFilters {
  constructor() {
    const openBtn = document.getElementById('filter-open');
    const closeBtn = document.getElementById('filter-close');
    const sidebar = document.getElementById('filter-sidebar');
    const overlay = document.getElementById('overlay');
    if (!sidebar) return;

    openBtn?.addEventListener('click', () => {
      sidebar.classList.add('open');
      overlay?.classList.add('active');
      document.body.classList.add('no-scroll');
    });
    closeBtn?.addEventListener('click', () => this.closeSidebar(sidebar, overlay));
    overlay?.addEventListener('click', () => this.closeSidebar(sidebar, overlay));

    document.querySelectorAll('.filter-accordion__header').forEach(h => {
      h.addEventListener('click', () => {
        h.closest('.filter-accordion__item').classList.toggle('open');
      });
    });
  }

  closeSidebar(sidebar, overlay) {
    sidebar.classList.remove('open');
    overlay?.classList.remove('active');
    document.body.classList.remove('no-scroll');
  }
}

// ── Search drawer ──────────────────────────────────────────────
class SearchDrawer {
  constructor() {
    const openBtn = document.getElementById('search-open');
    const closeBtn = document.getElementById('search-close');
    const drawer = document.getElementById('search-drawer');
    const overlay = document.getElementById('overlay');

    openBtn?.addEventListener('click', () => {
      drawer?.classList.add('open');
      overlay?.classList.add('active');
      document.body.classList.add('no-scroll');
      drawer?.querySelector('input')?.focus();
    });
    closeBtn?.addEventListener('click', () => {
      drawer?.classList.remove('open');
      overlay?.classList.remove('active');
      document.body.classList.remove('no-scroll');
    });
  }
}

// ── Scroll reveal ──────────────────────────────────────────────
class ScrollReveal {
  constructor(root = document) {
    const els = [...root.querySelectorAll('.reveal')].filter(el => !el.classList.contains('is-visible'));
    if (!els.length) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) {
      els.forEach(el => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const delay = el.dataset.revealDelay;
        if (delay) el.style.setProperty('--reveal-delay', delay + 'ms');
        el.classList.add('is-visible');
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach(el => io.observe(el));
  }
}

// Re-inizializza reveal quando l'editor Shopify aggiorna una sezione
document.addEventListener('shopify:section:load', (e) => {
  new ScrollReveal(e.target);
});

// ── Mega Menu (desktop hover with delayed close) ───────────────
class MegaMenu {
  constructor() {
    const wraps = document.querySelectorAll('[data-nav-mega]');
    wraps.forEach(wrap => {
      const trigger = wrap.querySelector('.nav-mega__trigger');
      const panel = wrap.querySelector('.nav-mega__panel');
      if (!trigger || !panel) return;

      let closeTimer = null;
      const open = () => {
        clearTimeout(closeTimer);
        wrap.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
      };
      const close = () => {
        wrap.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
      };
      const scheduleClose = () => {
        clearTimeout(closeTimer);
        closeTimer = setTimeout(close, 220);
      };

      // Hover on either trigger or panel keeps it open
      [trigger, panel].forEach(el => {
        el.addEventListener('mouseenter', open);
        el.addEventListener('mouseleave', scheduleClose);
      });

      // Keyboard / focus
      trigger.addEventListener('focus', open);
      wrap.addEventListener('focusout', (e) => {
        if (!wrap.contains(e.relatedTarget)) close();
      });

      // ESC to close
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') close();
      });
    });
  }
}

// ── Mobile sub-menu toggle (Catalogo) ──────────────────────────
class MobileSubMenu {
  constructor() {
    document.querySelectorAll('[data-mobile-submenu-toggle]').forEach(btn => {
      btn.addEventListener('click', () => {
        const group = btn.closest('[data-mobile-submenu]');
        const isOpen = group.classList.toggle('open');
        btn.setAttribute('aria-expanded', String(isOpen));
      });
    });
  }
}

// ── Cinematic header reveal (homepage only) ────────────────────
class CinematicHeader {
  constructor() {
    if (!document.body.classList.contains('home-cinematic')) return;
    const mq = window.matchMedia('(min-width: 1024px)');
    if (!mq.matches) return;
    const threshold = () => Math.max(80, window.innerHeight * 0.6);
    let raf = null;
    const update = () => {
      raf = null;
      if (window.scrollY > threshold()) {
        document.body.classList.add('header-revealed');
      } else {
        document.body.classList.remove('header-revealed');
      }
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }
}

// ── Init ───────────────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', () => {
  new CartDrawer();
  new AddToCart();
  new MobileNav();
  new QuantityStepper();
  new VariantSelector();
  new ProductGallery();
  new Accordion();
  new CollectionFilters();
  new SearchDrawer();
  new ScrollReveal();
  new CinematicHeader();
  new MegaMenu();
  new MobileSubMenu();
  new Lightbox();
});
